import React, { useRef, useEffect, useState } from 'react';
import './Story.css';

const Story = () => {
  const scrollContainerRef = useRef(null);
  const scrollPosRef = useRef(0);
  const [isPaused, setIsPaused] = useState(false);

  const storyImages = [
    { id: 1, path: '/images/story1.jpg' },
    { id: 2, path: '/images/story2.jpg' },
    { id: 3, path: '/images/story3.jpg' },
    { id: 4, path: '/images/story4.jpg' }
  ];

  // Triple the images for a very long seamless-feeling scroll
  const displayImages = [...storyImages, ...storyImages, ...storyImages];

  // Initialize scroll position to the middle set of images for seamless start
  useEffect(() => {
    if (scrollContainerRef.current) {
      const { scrollWidth } = scrollContainerRef.current;
      const startPos = scrollWidth / 3;
      scrollContainerRef.current.scrollLeft = startPos;
      scrollPosRef.current = startPos;
    }
  }, []);

  useEffect(() => {
    let animationFrameId;
    let lastTime = performance.now();
    const speed = 0.04; // Adjust speed as needed

    const animate = (time) => {
      const deltaTime = time - lastTime;
      lastTime = time;

      if (!isPaused && scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const { scrollWidth, clientWidth } = container;
        
        // Track precise position in a ref to avoid integer rounding issues
        scrollPosRef.current += speed * deltaTime;

        // Seamless loop: if we've scrolled past the second set, reset to the first set
        if (scrollPosRef.current >= (scrollWidth * 2) / 3) {
          scrollPosRef.current = scrollWidth / 3;
        }
        
        container.scrollLeft = scrollPosRef.current;
      } else if (scrollContainerRef.current) {
        // Sync the ref with the actual scroll position when paused (user manual scroll)
        scrollPosRef.current = scrollContainerRef.current.scrollLeft;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isPaused]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 310; // Card width + gap
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      
      // Briefly pause auto-scroll after manual interaction
      setIsPaused(true);
      setTimeout(() => setIsPaused(false), 3000);
    }
  };

  return (
    <section id="story" className="story-section">
      <div className="story-container">
        <div className="story-content-left">
          <h2 className="story-main-title">OUR SACRED STORY</h2>
          <h3 className="story-subtitle">Ayodhya: More Than a Place, A Way of Living</h3>
          <div className="story-description poetic-format">
            <p className="story-text">
              This is the land of Shri Ram and Mata Sita, 
              where values mattered more than comfort
              and where daily life followed quiet discipline.
              Mornings began with temple bells and clean homes.
              Food was prepared with care, offered first, and then shared.
              Nothing entered the house without thought.
            </p>
            <p className="story-text">
              Faith here was not loud. 
              It was lived through honesty, patience, and routine.
              When Shri Ram left Ayodhya, the city did not change its ways.
              Bharat ruled by placing Ram’s sandals on the throne. Today, life is faster.
              We choose quickly and trust less.

            </p>
            <p className="story-text">
              Ayodhya reminds you,
              showing that values must remain, even when people change.
              When care came before speed
              and purity was a habit, not a claim.

              Some values never grow old.

            </p>
          </div>
        </div>
        
        <div className="story-carousel-right">
          <div className="carousel-main-wrapper">
            <div 
              className="carousel-viewport" 
              ref={scrollContainerRef}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="carousel-track">
                {displayImages.map((img, index) => (
                  <div key={`${img.id}-${index}`} className="carousel-card">
                    <img 
                      src={img.path} 
                      alt={`Ayodhya Dairy Story ${img.id}`} 
                      className="carousel-img"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            <div className="carousel-navigation">
              <button 
                className="nav-btn prev" 
                onClick={() => scroll('left')}
                aria-label="Previous slide"
              >
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                </svg>
              </button>
              <button 
                className="nav-btn next" 
                onClick={() => scroll('right')}
                aria-label="Next slide"
              >
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Story;
